import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { whatsappUrl } from "../components/SiteShell";

export type LegalTab = "privacy" | "terms" | "data-deletion";

interface LegalPageProps {
  initialTab?: LegalTab;
}

export function LegalPage({ initialTab = "privacy" }: LegalPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<LegalTab>(initialTab);

  // Form states for data deletion request
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const titles: Record<LegalTab, string> = {
      privacy: "Política de Privacidade | Executive Jiu-Jitsu",
      terms: "Termos de Uso | Executive Jiu-Jitsu",
      "data-deletion": "Exclusão de Conta e Dados | Executive Jiu-Jitsu",
    };
    document.title = titles[activeTab] || "Legal | Executive Jiu-Jitsu";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const handleTabChange = (tab: LegalTab) => {
    setActiveTab(tab);
    setFormSubmitted(false);
    setErrorMessage("");
    const path = tab === "data-deletion" ? "/data-deletion" : `/${tab}`;
    navigate({ to: path as any });
  };

  const validateForm = () => {
    if (!fullName.trim() || !email.trim() || !email.includes("@") || !confirmed) {
      setErrorMessage(t("legal.dataDeletion.form.validationError"));
      return false;
    }
    setErrorMessage("");
    return true;
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const subject = encodeURIComponent(`Solicitação de Exclusão de Conta - ${fullName.trim()}`);
    const body = encodeURIComponent(
      `Prezada Equipe de Privacidade do Executive Jiu-Jitsu,\n\n` +
      `Eu, ${fullName.trim()}, venho por meio deste solicitar formalmente a exclusão permanente da minha conta e de todos os meus dados pessoais associados ao e-mail ${email.trim()} dos registros do aplicativo e plataformas do Executive Jiu-Jitsu.\n\n` +
      (reason.trim() ? `Motivo da solicitação: ${reason.trim()}\n\n` : "") +
      `Estou ciente de que esta ação é irreversível e removerá meu histórico de presenças e treinos.\n\n` +
      `Data da solicitação: ${new Intl.DateTimeFormat("pt-BR").format(new Date())}\n`
    );

    window.location.href = `mailto:contato@executivejiujitsu.com?subject=${subject}&body=${body}`;
    setFormSubmitted(true);
  };

  const handleWhatsappSubmit = () => {
    if (!validateForm()) return;

    const text = encodeURIComponent(
      `Olá, Equipe Executive Jiu-Jitsu! Gostaria de solicitar formalmente a exclusão permanente da minha conta e dos meus dados pessoais.\n\n` +
      `*Nome:* ${fullName.trim()}\n` +
      `*E-mail cadastrado:* ${email.trim()}\n` +
      (reason.trim() ? `*Motivo:* ${reason.trim()}\n` : "") +
      `Confirmo a ciência dos termos de eliminação definitiva.`
    );

    window.open(`${whatsappUrl}?text=${text}`, "_blank", "noopener,noreferrer");
    setFormSubmitted(true);
  };

  const privacySections = (t("legal.privacy.sections", { returnObjects: true }) as Array<{
    title: string;
    content: string;
  }>) || [];

  const termsSections = (t("legal.terms.sections", { returnObjects: true }) as Array<{
    title: string;
    content: string;
  }>) || [];

  const deletionSteps = (t("legal.dataDeletion.steps", { returnObjects: true }) as Array<{
    num: string;
    title: string;
    desc: string;
  }>) || [];

  const deletionTableRows = (t("legal.dataDeletion.tableRows", { returnObjects: true }) as Array<{
    category: string;
    action: string;
    retention: string;
  }>) || [];

  return (
    <main className="legal-page">
      {/* Header Banner */}
      <section className="legal-header-section">
        <div className="legal-header-container">
          <span className="legal-badge">{t("legal.badge")}</span>
          <h1>
            {activeTab === "privacy" && t("legal.privacy.title")}
            {activeTab === "terms" && t("legal.terms.title")}
            {activeTab === "data-deletion" && t("legal.dataDeletion.title")}
          </h1>
          <p className="legal-subtitle">
            {activeTab === "privacy" && t("legal.privacy.subtitle")}
            {activeTab === "terms" && t("legal.terms.subtitle")}
            {activeTab === "data-deletion" && t("legal.dataDeletion.subtitle")}
          </p>
          <div className="legal-meta">
            <span>{t("legal.lastUpdated")}</span>
            <span className="legal-meta-separator">•</span>
            <span>Google Play & Apple Store Compliant</span>
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <nav className="legal-tabs-nav" aria-label="Documentos Legais">
        <div className="legal-tabs-container">
          <button
            type="button"
            className={`legal-tab-btn ${activeTab === "privacy" ? "active" : ""}`}
            onClick={() => handleTabChange("privacy")}
            aria-selected={activeTab === "privacy"}
          >
            {t("legal.tabs.privacy")}
          </button>
          <button
            type="button"
            className={`legal-tab-btn ${activeTab === "terms" ? "active" : ""}`}
            onClick={() => handleTabChange("terms")}
            aria-selected={activeTab === "terms"}
          >
            {t("legal.tabs.terms")}
          </button>
          <button
            type="button"
            className={`legal-tab-btn ${activeTab === "data-deletion" ? "active" : ""}`}
            onClick={() => handleTabChange("data-deletion")}
            aria-selected={activeTab === "data-deletion"}
          >
            {t("legal.tabs.dataDeletion")}
          </button>
        </div>
      </nav>

      {/* Content Container */}
      <section className="legal-content-section">
        <div className="legal-container">
          {/* TAB 1: POLÍTICA DE PRIVACIDADE */}
          {activeTab === "privacy" && (
            <article className="legal-article">
              {privacySections.map((sec, idx) => (
                <div key={idx} className="legal-section-block">
                  <h2>{sec.title}</h2>
                  <div className="legal-section-body">
                    {sec.content.split("\n").map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}
            </article>
          )}

          {/* TAB 2: TERMOS DE USO */}
          {activeTab === "terms" && (
            <article className="legal-article">
              {termsSections.map((sec, idx) => (
                <div key={idx} className="legal-section-block">
                  <h2>{sec.title}</h2>
                  <div className="legal-section-body">
                    {sec.content.split("\n").map((paragraph, pIdx) => (
                      <p key={pIdx}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}
            </article>
          )}

          {/* TAB 3: EXCLUSÃO DE CONTA E DADOS */}
          {activeTab === "data-deletion" && (
            <article className="legal-article legal-deletion-article">
              <div className="legal-overview-box">
                <p>{t("legal.dataDeletion.overview")}</p>
              </div>

              {/* Steps */}
              <div className="legal-steps-section">
                <h2>{t("legal.dataDeletion.howToTitle")}</h2>
                <div className="legal-steps-grid">
                  {deletionSteps.map((st, idx) => (
                    <div key={idx} className="legal-step-card">
                      <span className="step-num">{st.num}</span>
                      <h3>{st.title}</h3>
                      <p>{st.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Table */}
              <div className="legal-table-section">
                <h2>{t("legal.dataDeletion.tableTitle")}</h2>
                <div className="legal-table-wrapper">
                  <table className="legal-retention-table">
                    <thead>
                      <tr>
                        <th>{t("legal.dataDeletion.tableHeaders.category")}</th>
                        <th>{t("legal.dataDeletion.tableHeaders.action")}</th>
                        <th>{t("legal.dataDeletion.tableHeaders.retention")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deletionTableRows.map((row, idx) => (
                        <tr key={idx}>
                          <td><strong>{row.category}</strong></td>
                          <td>
                            <span className="badge-tag">{row.action}</span>
                          </td>
                          <td>{row.retention}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Form */}
              <div className="legal-form-container" id="solicitar-exclusao">
                <div className="legal-form-header">
                  <h2>{t("legal.dataDeletion.form.title")}</h2>
                  <p>{t("legal.dataDeletion.form.desc")}</p>
                </div>

                {errorMessage && (
                  <div className="legal-form-alert error" role="alert">
                    {errorMessage}
                  </div>
                )}

                {formSubmitted && (
                  <div className="legal-form-alert success" role="status">
                    {t("legal.dataDeletion.form.successMsg")}
                  </div>
                )}

                <form onSubmit={handleEmailSubmit} className="legal-deletion-form">
                  <div className="form-group">
                    <label htmlFor="fullName">{t("legal.dataDeletion.form.nameLabel")} *</label>
                    <input
                      id="fullName"
                      type="text"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={t("legal.dataDeletion.form.namePlaceholder")}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">{t("legal.dataDeletion.form.emailLabel")} *</label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("legal.dataDeletion.form.emailPlaceholder")}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="reason">{t("legal.dataDeletion.form.reasonLabel")}</label>
                    <textarea
                      id="reason"
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t("legal.dataDeletion.form.reasonPlaceholder")}
                    />
                  </div>

                  <div className="form-checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        required
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                      />
                      <span>{t("legal.dataDeletion.form.consentLabel")}</span>
                    </label>
                  </div>

                  <div className="form-actions-group">
                    <button type="submit" className="button form-submit-btn">
                      {t("legal.dataDeletion.form.submitEmailBtn")}
                    </button>
                    <button
                      type="button"
                      className="button dark-button form-whatsapp-btn"
                      onClick={handleWhatsappSubmit}
                    >
                      {t("legal.dataDeletion.form.submitWhatsappBtn")}
                    </button>
                  </div>
                </form>
              </div>
            </article>
          )}

          {/* Quick Nav Footer between Legal Docs */}
          <div className="legal-doc-footer-links">
            <span>{t("nav.home")}: </span>
            <Link to="/" className="text-link light-link">
              executivejiujitsu.com
            </Link>
            <span className="separator">•</span>
            <Link to="/privacy" className="text-link light-link">
              {t("legal.tabs.privacy")}
            </Link>
            <span className="separator">•</span>
            <Link to="/terms" className="text-link light-link">
              {t("legal.tabs.terms")}
            </Link>
            <span className="separator">•</span>
            <Link to="/data-deletion" className="text-link light-link">
              {t("legal.tabs.dataDeletion")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
